import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/700.css';
import '@fontsource/spartan/400.css';
import '@fontsource/spartan/500.css';
import '@fontsource/spartan/600.css';
import '@fontsource/spartan/700.css';
import { createTheme } from '@mui/material/styles';

import { claimsToolPalette } from './theme/claimsToolPalette';

// copied over from the Member Portal's src/theme.ts
const brandColors = {
  darkCornflowerBlue: '#2D3E91',
  softPink: '#FFD1CC',
  seashell: '#FFF3EC',
  middleBlue: '#91CED2',
  lightCoral: '#F86969',
  darkGrey: '#403F3F',
  white: '#FFFFFF',
  powderBlue: '#ABE4E5',
};

const treatmentColors = {
  adoption: '#FFF6F6',
  eggDonation: '#FEA7A7',
  eggFreezing: '#FFE5E5',
  insemination: '#7CABAF',
  fertilityTesting: '#F4FAFA',
  fertilityAnalysisAndIVFPreparation: '#BDE2E4',
  ivf: '#437276',
  spermDonation: '#EDEFF9',
  spermFreezing: '#5361A6',
  surrogacy: '#2B3772',
  default: '#FEF0F0',
};

// copied over from the Member Portal's src/theme.ts
const theme = createTheme({
  spacing: 8,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: brandColors.darkGrey,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '1rem',
          lineHeight: 1.4,
          letterSpacing: '0.02rem',
        },
      },
    },
    MuiInput: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontWeight: '400',
        },
        standardSuccess: {
          color: '#614E1C',
          backgroundColor: '#E2F5E3',
        },
        filledSuccess: {
          color: '#448046',
          backgroundColor: '#E2F5E3',
          svg: {
            color: '#6ECC71',
          },
        },
        filledWarning: {
          color: '#614E1C',
          backgroundColor: '#FCEFCD',
          svg: {
            color: '#A18335',
          },
        },
        filledError: {
          color: '#631B16',
          backgroundColor: '#FEE1E1',
          svg: {
            color: '#F44336',
          },
        },
        filledInfo: {
          color: '#1B2557',
          backgroundColor: '#EDEFF9',
          svg: {
            color: '#2D3E91',
          },
        },
        standardInfo: {
          '& .MuiAlert-icon': {
            color: 'inherit',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            background: 'none',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '& .MuiTablePagination-displayedRows	': {
            fontSize: 14,
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: { letterSpacing: '0.02rem' },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderBottom: `solid 1px rgba(0, 0, 0, 0.12)`,
          boxShadow: 'none',
          '&::before': {
            backgroundColor: 'transparent',
          },
          '&$expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingRight: 16,
          backgroundColor: 'transparent',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '12px 16px 12px 0',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: brandColors.lightCoral,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          fontFamily: 'DM Sans',
          fontWeight: 600,
          fontSize: 14,
        },
        contained: {
          '&:hover': {
            boxShadow:
              '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14)',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#E9F5F6',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'primary',
        underline: 'hover',
      },
      styleOverrides: {
        underlineHover: {
          cursor: 'pointer',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 2,
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow:
            '0px 2px 1px -1px rgba(0, 0, 0, 0.2),' +
            ' 0px 1px 1px rgba(0, 0, 0, 0.14),' +
            ' 0px 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        padding: { paddingTop: 0, paddingBottom: 0 },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: 16,
        },
        primary: {
          fontSize: 16,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: 'DM Sans',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'DM Sans',
        },
      },
    },
  },
  palette: {
    claimsTool: claimsToolPalette,
    background: {
      default: brandColors.white,
    },
    primary: {
      main: brandColors.darkCornflowerBlue,
      light: '#BCC5EF',
      dark: '#081A67',
    },
    secondary: {
      main: brandColors.middleBlue,
      light: '#BAE4E7',
      dark: '#38999F',
      contrastText: brandColors.darkCornflowerBlue,
    },
    action: {
      hover: 'rgba(64, 63, 63, 0.08)',
    },
    text: {
      primary: brandColors.darkGrey,
      secondary: 'rgba(63, 64, 64, 0.54)', // darkGrey @ 54% opacity
      disabled: 'rgba(63, 64, 64, 0.38)', // darkGrey @ 38% opacity
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#F5CA57',
    },
    info: {
      main: '#2196F3',
      light: '#E3F2FD',
    },
    success: {
      main: '#6ECC71',
      light: '#f0faf1',
    },
    brandColors: brandColors,
    other: {
      treatmentColors,
    },
  },
  typography: {
    h1: {
      fontFamily: 'Spartan',
      fontWeight: 700,
      fontSize: 34,
      lineHeight: 1.5,
    },
    h2: {
      fontFamily: 'Spartan',
      fontWeight: 700,
      fontSize: 32,
      lineHeight: 1.4,
    },
    h3: {
      fontFamily: 'DM Sans',
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: 'Spartan',
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: 0.25,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: 'DM Sans',
      fontSize: 20,
      fontWeight: 500,
      lineHeight: 1.25,
      letterSpacing: '0.02rem',
    },
    h6: {
      fontFamily: 'Spartan',
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '0.02rem',
    },
    subtitle1: {
      fontFamily: 'DM Sans',
      fontWeight: 700,
      fontSize: 16,
      lineHeight: 1.25,
      letterSpacing: '0.02rem',
      color: brandColors.darkGrey,
    },
    subtitle2: {
      fontFamily: 'DM Sans',
      fontWeight: 500,
      fontSize: 14,
      lineHeight: 1.57,
      letterSpacing: 0.1,
      color: brandColors.darkGrey,
    },
    body1: {
      fontFamily: 'DM Sans',
      fontSize: 18,
      lineHeight: 1.2,
      letterSpacing: '0.02rem',
    },
    body2: {
      fontFamily: 'DM Sans',
      fontSize: 16,
      lineHeight: 1.4,
      letterSpacing: '0.02rem',
    },
    caption: {
      fontFamily: 'DM Sans',
      fontSize: 14,
      lineHeight: 1.6,
      letterSpacing: 0.4,
    },
    overline: {
      fontFamily: 'DM Sans',
    },
  },
});

export default theme;
